import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../../contexts/AppContext";

export const FileExplorer = () => {
  const { service, addNotification } = useApp();
  const [driveId, setDriveId] = useState(null);
  const [path, setPath] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    service.getDrives().then(d => {
      const drive = d?.value?.find(dr => dr.name === "ContractDocuments");
      if (!drive) { setError("ContractDocuments library not found."); setLoading(false); return null; }
      setDriveId(drive.id);
      return service.getFolderContents(drive.id, "");
    }).then(d => { if (d) setItems(d?.value || []); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const currentFolderPath = path.map(p => p.name).join("/");

  const reload = async () => {
    setLoading(true);
    try {
      const d = await service.getFolderContents(driveId, currentFolderPath);
      setItems(d?.value || []);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const navigate = async (item) => {
    if (!item.folder) { window.open(item.webUrl, "_blank"); return; }
    setLoading(true);
    const newPath = [...path, { name: item.name, id: item.id }];
    try {
      const d = await service.getFolderContents(driveId, newPath.map(p => p.name).join("/"));
      setItems(d?.value || []); setPath(newPath);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const goTo = async (idx) => {
    setLoading(true);
    const newPath = path.slice(0, idx + 1);
    try {
      const d = await service.getFolderContents(driveId, newPath.map(p => p.name).join("/"));
      setItems(d?.value || []); setPath(newPath);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const goRoot = async () => {
    setLoading(true);
    try { const d = await service.getFolderContents(driveId, ""); setItems(d?.value || []); setPath([]); }
    catch (e) { setError(e.message); }
    setLoading(false);
  };

  const uploadFiles = async (files) => {
    if (!files.length || !driveId) return;
    setUploading(true);
    let success = 0;
    try {
      for (const file of files) {
        const buf = await file.arrayBuffer();
        await service.uploadFile(driveId, currentFolderPath || "Uploads", file.name, buf);
        success++;
      }
      await reload();
      addNotification(`${success} file${success > 1 ? "s" : ""} uploaded successfully`, "success");
    } catch (err) {
      addNotification("Upload failed: " + err.message, "error");
    }
    setUploading(false);
  };

  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current++; if (e.dataTransfer.items?.length) setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current--; if (dragCounter.current === 0) setDragging(false); };
  const handleDragOver  = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragging(false); dragCounter.current = 0;
    await uploadFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = async (e) => {
    await uploadFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const getIcon = (item) => {
    if (item.folder) return String.fromCodePoint(0x1F4C1);
    const ext = (item.name || "").split(".").pop().toLowerCase();
    const map = { pdf:"\u{1F4D5}", docx:"\u{1F4D8}", doc:"\u{1F4D8}", xlsx:"\u{1F4D7}", xls:"\u{1F4D7}", png:"\u{1F5BC}", jpg:"\u{1F5BC}" };
    return map[ext] || String.fromCodePoint(0x1F4C4);
  };

  const fmtSize = (b) => !b ? "" : b < 1024 ? b+"B" : b < 1048576 ? Math.round(b/1024)+"KB" : (b/1048576).toFixed(1)+"MB";

  return (
    <div onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop} className="relative min-h-96">

      {/* Drop overlay */}
      {dragging && (
        <div className="fixed inset-0 z-50 bg-dark-bg-tertiary/88 flex flex-col items-center justify-center border-2 border-dashed border-accent pointer-events-none">
          <div className="text-5xl">📂</div>
          <div className="text-2xl font-semibold text-accent font-serif mt-3">Drop files to upload</div>
          <div className="text-base text-dark-text-secondary mt-1.5">into: /{currentFolderPath || "Root"}</div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="font-serif text-3xl font-medium text-light-text dark:text-dark-text mb-1">File Explorer</h1>
        <p className="text-light-text-secondary dark:text-dark-text-secondary m-0">Browse your contract documents. Drag and drop files anywhere to upload to the current folder.</p>
        <div className="h-0.5 bg-gradient-to-r from-accent to-transparent mt-2" />
      </div>

      {/* Breadcrumb + Upload */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 flex items-center gap-1.5 p-2.5 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg flex-wrap">
          <button onClick={goRoot} className={`bg-none border-none cursor-pointer text-base font-mono p-0 ${path.length ? "text-light-text-secondary dark:text-dark-text-secondary" : "text-accent"}`}>Root</button>
          {path.map((p, i) => (
            <React.Fragment key={i}>
              <span className="text-light-border dark:text-dark-border">/</span>
              <button onClick={() => goTo(i)} className={`bg-none border-none cursor-pointer text-base font-mono p-0 ${i===path.length-1 ? "text-accent" : "text-light-text-secondary dark:text-dark-text-secondary"}`}>{p.name}</button>
            </React.Fragment>
          ))}
        </div>
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
          className={`px-4 py-2.5 bg-accent/20 border border-accent/50 rounded-lg text-accent cursor-pointer text-base font-sans font-medium whitespace-nowrap ${uploading ? "opacity-60 cursor-not-allowed" : ""}`}>
          {uploading ? "Uploading..." : "⬆ Upload Files"}
        </button>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
      </div>

      {/* Table */}
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg overflow-hidden">
        {error ? <div className="p-12 text-center text-light-text-secondary dark:text-dark-text-secondary">{error}</div>
        : loading ? <div className="p-12 text-center text-light-text-secondary dark:text-dark-text-secondary">Loading...</div>
        : items.length === 0 ? (
          <div className="p-16 text-center">
            <div className="text-4xl mb-3">📂</div>
            <div className="text-light-text-secondary dark:text-dark-text-secondary mb-1">This folder is empty</div>
            <div className="text-base text-light-border dark:text-dark-border">Drag files here or use the Upload button to add documents</div>
          </div>
        ) : (
          <table className="w-full border-collapse text-base">
            <thead>
              <tr className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
                {["Name","Type","Size","Last Modified"].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-base font-medium text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wider border-b border-light-border dark:border-dark-border">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...items].sort((a,b) => (b.folder?1:0)-(a.folder?1:0)).map((item, i) => (
                <tr key={i} onClick={() => navigate(item)} className="cursor-pointer border-b border-light-border dark:border-dark-border hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className="text-xl">{getIcon(item)}</span>
                      <span className={item.folder ? "text-accent font-semibold" : "text-light-text dark:text-dark-text"}>{item.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-base text-light-text-secondary dark:text-dark-text-secondary">{item.folder?"Folder":(item.name||"").split(".").pop().toUpperCase()}</td>
                  <td className="px-4 py-2.5 text-base text-light-text-secondary dark:text-dark-text-secondary font-mono">{item.folder?"":fmtSize(item.size)}</td>
                  <td className="px-4 py-2.5 text-base text-light-text-secondary dark:text-dark-text-secondary">{item.lastModifiedDateTime?new Date(item.lastModifiedDateTime).toLocaleDateString():""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};